import { bem, bitmask, BROADCAST_STATE, MUTES } from '@socialive/common';
import PropTypes from 'prop-types';
import { forwardRef, useMemo } from 'react';
import VideoStream from '../../Atoms/VideoStream';
import CameraOffIcon from '../../Icons/CameraOffIcon';
import GuestUserInfo from './GuestUserInfo';
import GuestUserLocation from './GuestUserLocation';
import Guides from './Guides';
import './streamCard.css';

/*
#tailwind
 streamCard streamCard--isPinned streamCard--isDragging streamCard--isLive streamCard--isDragging--swap streamCard--drag-hover--insert-before
 streamCard--drag-hover--swap streamCard--drag-hover--insert-after streamCard__overlayTopRowRight
 streamCard__overlayTopRowRight--permanent
*/

const StreamCard = forwardRef(
  (
    {
      className,
      displayName,
      stream,
      mute,
      isDirector,
      bottomRightOverlay,
      topRightOverlay,
      centerOverlay,
      locationState,
      isRecording,
      isHostUser,
      isMe,
      isMirrored,
      hasGuides,
      isDragging,
      isPinned,
      isDragOver,
      dropType,
      isVideoMuted,
      slideUrl,
      testId,
      ...other
    },
    ref
  ) => {
    const { isMicMuted } = useMemo(
      () => ({
        isMicMuted: bitmask(mute).some(MUTES.MICROPHONE),
      }),
      [mute]
    );

    const userInfoTestId = testId && `${testId}-user-info`;
    const userLocationTestId = testId && `${testId}-user-location`;
    const videoStreamTestId = testId && `${testId}-video-stream`;
    const slideTestId = testId && `${testId}-slide`;

    return (
      <div
        data-testid={testId}
        ref={ref}
        className={bem`streamCard--${{
          isDragging,
          isPinned,
          isLive: locationState === BROADCAST_STATE.LIVE,
        }} streamCard--${{
          '': 0,
          'drag-hover': isDragOver,
        }}--${{
          [dropType]: 1,
        }}  ${className}`}
        {...other}
      >
        {stream && (
          <VideoStream
            isMirrored={isMirrored}
            stream={stream}
            isMuted={!!mute}
            isMasked={locationState === BROADCAST_STATE.BACKSTAGE}
            testId={videoStreamTestId}
          />
        )}

        {slideUrl && (
          <img
            src={slideUrl}
            alt={`${displayName}'s Slide`}
            className={bem`streamCard__imageStream--${{
              isMasked: locationState === BROADCAST_STATE.BACKSTAGE,
            }}`}
            data-testid={slideTestId}
          />
        )}
        {hasGuides && stream && <Guides />}
        <div className="streamCard__overlay">
          {isVideoMuted && (
            <div className="streamCard__videoMutedContainer">
              <CameraOffIcon className="streamCard__videoMutedIcon" />
            </div>
          )}
          <div className="streamCard__overlayTopRow">
            <div className="streamCard__overlayTopRowLeft">
              <GuestUserLocation
                context={locationState}
                className="streamCard__guestUserLocation"
                testId={userLocationTestId}
              />
            </div>
            <div className={bem`streamCard__overlayTopRowRight--${{ permanent: slideUrl && isMe }}`}>
              {topRightOverlay}
            </div>
          </div>
          <div className="steamCard_overlayCenterRow">{centerOverlay}</div>
          <div className="streamCard__overlayBottomRow">
            <GuestUserInfo
              name={displayName}
              isDirector={isDirector}
              isMuted={isMicMuted}
              isRecording={isRecording}
              isHostUser={isHostUser}
              testId={userInfoTestId}
            />
            {bottomRightOverlay}
          </div>
        </div>
      </div>
    );
  }
);

StreamCard.propTypes = {
  className: PropTypes.string,
  centerOverlay: PropTypes.node,
  bottomRightOverlay: PropTypes.node,
  topRightOverlay: PropTypes.node,
  displayName: PropTypes.string,
  isDirector: PropTypes.bool,
  isHostUser: PropTypes.bool,
  isRecording: PropTypes.bool,
  isMe: PropTypes.bool,
  isMirrored: PropTypes.bool,
  mute: PropTypes.number, // @socialive/broadcast MUTES bitmask
  locationState: PropTypes.oneOf(Object.values(BROADCAST_STATE)),
  hasGuides: PropTypes.bool,
  stream: PropTypes.instanceOf(MediaStream),
  isDragging: PropTypes.bool,
  isPinned: PropTypes.bool,
  isDragOver: PropTypes.bool,
  dropType: PropTypes.string,
  isVideoMuted: PropTypes.bool,
  slideUrl: PropTypes.string,
  testId: PropTypes.string,
};

StreamCard.defaultProps = {
  className: undefined,
  centerOverlay: null,
  bottomRightOverlay: null,
  topRightOverlay: null,
  displayName: null,
  isDirector: null,
  isHostUser: false,
  isRecording: false,
  isMe: false,
  isMirrored: false,
  mute: MUTES.NONE,
  locationState: undefined,
  hasGuides: false,
  stream: undefined,
  isDragging: false,
  isPinned: false,
  isDragOver: false,
  dropType: '',
  isVideoMuted: false,
  slideUrl: '',
  testId: undefined,
};
export default StreamCard;
